import { useContext } from "react";
import { Col, Form, Input, Row, Select, Switch, Typography } from "antd";
import { Controller, UseFormClearErrors, UseFormSetValue } from "react-hook-form";
import { IdcardOutlined, EnvironmentOutlined } from "@ant-design/icons";

import { ICityFormStepProps, ICityInputs } from "../../../../interfaces";
import { ICityData } from "../../../../../../interfaces";
import { ThemeContext } from "../../../../../../contexts/ThemeContext";

import useRegisterStep from "./useRegisterStep";

const { Text } = Typography;

interface IProps extends ICityFormStepProps {
    colInputs: any;
    setValue: UseFormSetValue<ICityInputs>;
    clearErrors: UseFormClearErrors<ICityInputs>;
    selectedCity: {
        uf: string | undefined;
        city: string | undefined;
    };
    cities: Array<ICityData>;
    isLoadingCities: boolean;
    isEditing: boolean;
}

export default function RegisterStep({
    colInputs,
    errors,
    control,
    setValue,
    cities,
    isLoadingCities,
    selectedCity,
    clearErrors,
    isEditing,
}: IProps) {
    const { filterOption, STATES } = useRegisterStep();
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";

    const sectionTitleStyle: React.CSSProperties = {
        fontSize: 13,
        fontWeight: 600,
        color: isDark ? "#94a3b8" : "#64748b",
        textTransform: "uppercase",
        letterSpacing: "0.4px",
        marginBottom: 16,
        display: "flex",
        alignItems: "center",
        gap: 6,
    };

    return (
        <div>
            {/* Seção 1: Localização e Dados Oficiais */}
            <div style={{ marginBottom: 24 }}>
                <div style={sectionTitleStyle}>
                    <EnvironmentOutlined style={{ color: "#1677ff" }} />
                    <span>Localização e Identificação do Município</span>
                </div>

                <Row gutter={[16, 12]}>
                    <Col xs={24} sm={24} md={12} lg={6}>
                        <Form.Item
                            label="Estado (UF)"
                            required
                            validateStatus={errors.uf ? "error" : "success"}
                            help={errors.uf?.message}
                        >
                            <Controller
                                name="uf"
                                control={control}
                                render={({ field: { onChange, ...field } }) => (
                                    <Select
                                        {...field}
                                        options={STATES.map((state) => ({
                                            value: state.uf,
                                            label: `${state.name} - ${state.uf}`,
                                        }))}
                                        onChange={(e) => {
                                            onChange(e);
                                            setValue("city", "");
                                            setValue("cnpj", "");
                                            setValue("name", "");
                                        }}
                                        placeholder="Selecione o Estado"
                                        showSearch
                                        filterOption={filterOption}
                                        disabled={isEditing}
                                        style={{ width: "100%" }}
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={12} lg={10}>
                        <Form.Item
                            label="Município"
                            required
                            validateStatus={errors.city ? "error" : "success"}
                            help={errors.city?.message}
                        >
                            <Controller
                                name="city"
                                control={control}
                                render={({ field: { onChange, ...field } }) => (
                                    <Select
                                        {...field}
                                        onChange={(e) => {
                                            const cityId = Number(e);
                                            const foundCity = cities.find(
                                                (city) => city.id_ibge === cityId
                                            );

                                            if (!foundCity) return;

                                            onChange(foundCity.name);
                                            setValue("cnpj", foundCity.cnpj);
                                            setValue("name", `${foundCity.name}`);
                                            clearErrors(["cnpj", "name"]);
                                        }}
                                        options={cities.map((city) => ({
                                            value: city.id_ibge,
                                            label: city.name,
                                        }))}
                                        placeholder={
                                            !selectedCity.uf
                                                ? "Primeiro selecione o Estado"
                                                : "Selecione o Município"
                                        }
                                        showSearch
                                        filterOption={filterOption}
                                        loading={isLoadingCities}
                                        disabled={
                                            !selectedCity.uf ||
                                            isLoadingCities ||
                                            isEditing
                                        }
                                        style={{ width: "100%" }}
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={24} md={12} lg={8}>
                        <Form.Item
                            label="CNPJ do Município"
                            required
                            validateStatus={errors.cnpj ? "error" : "success"}
                            help={errors.cnpj?.message}
                        >
                            <Controller
                                name="cnpj"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="CNPJ preenchido automaticamente"
                                        disabled
                                        style={{ width: "100%" }}
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </div>

            {/* Seção 2: Dados Administrativos */}
            <div style={{ marginBottom: 12 }}>
                <div style={sectionTitleStyle}>
                    <IdcardOutlined style={{ color: "#722ed1" }} />
                    <span>Dados Administrativos e Parâmetros</span>
                </div>

                <Row gutter={[16, 12]}>
                    <Col xs={24} sm={24} md={16} lg={16}>
                        <Form.Item
                            label="Nome do Órgão / Prefeitura"
                            required
                            validateStatus={errors.name ? "error" : "success"}
                            help={errors.name?.message}
                        >
                            <Controller
                                name="name"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="Ex: Prefeitura Municipal de ..."
                                        disabled={!selectedCity.city && !isEditing}
                                        style={{ width: "100%" }}
                                    />
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item
                            label="Ambiente de Testes"
                            validateStatus={errors.is_in_test ? "error" : "success"}
                            help={errors.is_in_test?.message}
                        >
                            <Controller
                                name="is_in_test"
                                control={control}
                                render={({ field }) => (
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, height: 32 }}>
                                        <Switch
                                            {...field}
                                            onChange={(e) => field.onChange(e)}
                                            checked={field.value}
                                            checkedChildren="Sim"
                                            unCheckedChildren="Não"
                                        />
                                        <Text style={{ fontSize: 13, color: field.value ? "#faad14" : (isDark ? "#8c8c8c" : "#64748b") }}>
                                            {field.value ? "Em Testes" : "Produção"}
                                        </Text>
                                    </div>
                                )}
                            />
                        </Form.Item>
                    </Col>

                    <Col xs={24} sm={12} md={8} lg={4}>
                        <Form.Item
                            label="Endereço Obrigatório"
                            validateStatus={errors.is_address_required ? "error" : "success"}
                            help={errors.is_address_required?.message}
                        >
                            <Controller
                                name="is_address_required"
                                control={control}
                                render={({ field }) => (
                                    <div style={{ display: "flex", alignItems: "center", gap: 10, height: 32 }}>
                                        <Switch
                                            {...field}
                                            onChange={(e) => field.onChange(e)}
                                            checked={field.value}
                                            checkedChildren="Sim"
                                            unCheckedChildren="Não"
                                        />
                                        <Text style={{ fontSize: 13, color: isDark ? "#8c8c8c" : "#64748b" }}>
                                            {field.value ? "Exigir" : "Opcional"}
                                        </Text>
                                    </div>
                                )}
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </div>
        </div>
    );
}